import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Calculator, Download, TrendingUp, TrendingDown, Receipt, FileText, Mail, Send } from 'lucide-react';
import { useData } from '../context/DataContext';
import { carsysApi } from '../api/carsysApi';
import { toast } from 'sonner';
import { useT } from '../context/LanguageContext';
import PrintBoekhoudRapport from '../components/PrintBoekhoudRapport';

const eur = (n) => (Number(n) || 0).toLocaleString('nl-NL', { style: 'currency', currency: 'EUR' });

export default function Boekhouding() {
  const { t } = useT();
  const { bonnen, kosten, handelAutos, gegevens } = useData();
  const [showRapport, setShowRapport] = useState(false);
  const [boekhouder, setBoekhouder] = useState({ autoKwartaalrapport: false, accountantEmail: '', lastQuarterReportSent: null });
  const [boekhouderOpen, setBoekhouderOpen] = useState(false);
  const [savingBh, setSavingBh] = useState(false);
  const [sendingBh, setSendingBh] = useState(false);

  useEffect(() => { carsysApi.getRapportSettings().then(setBoekhouder).catch(() => {}); }, []);

  const saveBoekhouder = async (next) => {
    setSavingBh(true);
    try { const saved = await carsysApi.setRapportSettings(next); setBoekhouder(saved); toast.success(t('Opgeslagen', 'Opgeslagen')); }
    catch (e) { toast.error(t('Opslaan mislukt', 'Opslaan mislukt'), { description: e.response?.data?.detail || e.message }); }
    finally { setSavingBh(false); }
  };
  const huidigJaar = new Date().getFullYear();
  const [jaar, setJaar] = useState(huidigJaar);
  const [kwartaal, setKwartaal] = useState('jaar'); // 'Q1'..'Q4' | 'jaar'

  const jaren = useMemo(() => {
    const ys = new Set([huidigJaar]);
    [...bonnen, ...kosten].forEach(x => { const y = parseInt(String(x.datum || '').slice(0, 4)); if (y) ys.add(y); });
    return [...ys].sort((a, b) => b - a);
  }, [bonnen, kosten, huidigJaar]);

  const [from, to] = useMemo(() => {
    if (kwartaal === 'jaar') return [new Date(jaar, 0, 1), new Date(jaar, 11, 31, 23, 59, 59)];
    const q = parseInt(kwartaal.slice(1)) - 1;
    return [new Date(jaar, q * 3, 1), new Date(jaar, q * 3 + 3, 0, 23, 59, 59)];
  }, [jaar, kwartaal]);

  const inRange = useCallback((d) => { try { const x = new Date(d); return x >= from && x <= to; } catch { return false; } }, [from, to]);

  // --- OMZET (verkoopfacturen) ---
  const omzetBonnen = useMemo(() => bonnen.filter(b => (b.type === 'Factuur' || b.type === 'Werkbon') && inRange(b.datum)), [bonnen, inRange]);
  const omzetExcl = omzetBonnen.reduce((s, b) => s + ((Number(b.totaal) || 0) - (Number(b.btw) || 0)), 0);
  const verschuldigdeBtw = omzetBonnen.reduce((s, b) => s + (Number(b.btw) || 0), 0);

  // --- KOSTEN (inkoop) ---
  const kostenInRange = useMemo(() => kosten.filter(k => inRange(k.datum)), [kosten, inRange]);
  const kostenExcl = kostenInRange.reduce((s, k) => s + (Number(k.bedragExcl) || 0), 0);
  const voorbelasting = kostenInRange.reduce((s, k) => s + (Number(k.btwBedrag) || 0), 0);

  // --- AUTOHANDEL marge ---
  const verkochteAutos = useMemo(() => handelAutos.filter(a => a.status === 'Verkocht' && (!a.verkoopDatum || inRange(a.verkoopDatum))), [handelAutos, inRange]);
  const autoMarge = verkochteAutos.reduce((s, a) => s + ((Number(a.verkoopPrijs) || 0) - (Number(a.inkoopPrijs) || 0)), 0);

  // --- BTW per tarief ---
  const btwOmzetPerTarief = useMemo(() => {
    const m = {};
    omzetBonnen.forEach(b => {
      const tarief = b.btwPercentage != null ? b.btwPercentage : 21;
      const netto = (Number(b.totaal) || 0) - (Number(b.btw) || 0);
      if (!m[tarief]) m[tarief] = { netto: 0, btw: 0 };
      m[tarief].netto += netto; m[tarief].btw += (Number(b.btw) || 0);
    });
    return m;
  }, [omzetBonnen]);

  const kostenPerCategorie = useMemo(() => {
    const m = {};
    kostenInRange.forEach(k => { const c = k.categorie || 'Overig'; m[c] = (m[c] || 0) + (Number(k.bedragExcl) || 0); });
    return Object.entries(m).sort((a, b) => b[1] - a[1]);
  }, [kostenInRange]);

  const btwSaldo = verschuldigdeBtw - voorbelasting;
  const opbrengsten = omzetExcl + autoMarge;
  const resultaat = opbrengsten - kostenExcl;

  const periodeLabel = kwartaal === 'jaar' ? `${t('Heel jaar', 'Heel jaar')} ${jaar}` : `${kwartaal} ${jaar}`;

  const exportCsv = () => {
const rows = [
  [`MonteurCentraal - ${t('Boekhouding', 'Boekhouding')} ${periodeLabel}`],
  [],
  [t('BTW-AANGIFTE', 'BTW-AANGIFTE')],
  [t('Verschuldigde BTW (over omzet)', 'Verschuldigde BTW (over omzet)'), verschuldigdeBtw.toFixed(2)],
  [t('Voorbelasting (over inkopen)', 'Voorbelasting (over inkopen)'), voorbelasting.toFixed(2)],
  [
    btwSaldo >= 0
      ? t('Te betalen aan Belastingdienst', 'Te betalen aan Belastingdienst')
      : t('Terug te ontvangen', 'Terug te ontvangen'),
    Math.abs(btwSaldo).toFixed(2)
  ],
  [],
  [t('WINST & VERLIES', 'WINST & VERLIES')],
  [t('Omzet (excl. BTW)', 'Omzet (excl. BTW)'), omzetExcl.toFixed(2)],
  [t('Autohandel marge', 'Autohandel marge'), autoMarge.toFixed(2)],
  [t('Totale opbrengsten', 'Totale opbrengsten'), opbrengsten.toFixed(2)],
  [t('Kosten (excl. BTW)', 'Kosten (excl. BTW)'), kostenExcl.toFixed(2)],
  [t('Resultaat (winst/verlies)', 'Resultaat (winst/verlies)'), resultaat.toFixed(2)],
  [],
  [t('Kosten per categorie', 'Kosten per categorie')],
  ...kostenPerCategorie.map(([c, b]) => [c, b.toFixed(2)]),
];
    const csv = rows.map(r => r.map(c => { const s = String(c ?? ''); return /[",;\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; }).join(';')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `boekhouding-${periodeLabel.replace(/\s/g, '-')}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success(t('Geëxporteerd', 'Geëxporteerd'));
  };

  const downloadAuditfile = async () => {
    try {
      const blob = await carsysApi.downloadAuditfile(jaar);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `auditfile-${jaar}.xaf`; a.click();
      URL.revokeObjectURL(url);
      toast.success(t('Auditfile gedownload', 'Auditfile gedownload'), { description: `XAF 3.2 · ${jaar}` });
    } catch (e) {
      toast.error(t('Download mislukt', 'Download mislukt'), { description: e.response?.data?.detail || e.message });
    }
  };

  const downloadBtwAangifte = async () => {
    const q = kwartaal === 'jaar' ? (Math.floor(new Date().getMonth() / 3) + 1) : parseInt(kwartaal.slice(1));
    try {
      const blob = await carsysApi.downloadBtwAangifte(jaar, q);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `btw-aangifte-${jaar}-Q${q}.xbrl`; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(t('BTW-aangifte gedownload', 'BTW-aangifte gedownload'), { description: `XBRL · ${jaar} Q${q} (concept)` });
    } catch (e) {
      toast.error(t('Download mislukt', 'Download mislukt'), { description: e.response?.data?.detail || e.message });
    }
  };

  return (
    <div data-testid="boekhouding-page">
      <div className="cs-page-header">
        <div><h2>{t('Boekhouding — BTW & Winst', 'Boekhouding — BTW & Winst')}</h2><p>{t('BTW-aangifte overzicht en winst- & verliesrekening per periode', 'BTW-aangifte overzicht en winst- & verliesrekening per periode')}</p></div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="cs-btn" data-testid="boekhouding-export" onClick={exportCsv}><Download size={14} /> {t('Exporteren (CSV)', 'Exporteren (CSV)')}</button>
          <button className="cs-btn" data-testid="boekhouding-auditfile" onClick={downloadAuditfile}><FileText size={14} /> {t('Auditfile (XAF)', 'Auditfile (XAF)')}</button>
          <button className="cs-btn" data-testid="boekhouding-btw-aangifte" onClick={downloadBtwAangifte}><FileText size={14} /> {t('BTW-aangifte (XBRL)', 'BTW-aangifte (XBRL)')}</button>
          <button className="cs-btn primary" data-testid="boekhouding-rapport" onClick={() => setShowRapport(true)}><FileText size={14} /> {t('Rapport (PDF)', 'Rapport (PDF)')}</button>
        </div>
      </div>

      <div className="cs-toolbar" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <select className="cs-input" data-testid="boekhouding-jaar" style={{ maxWidth: 120 }} value={jaar} onChange={e => setJaar(parseInt(e.target.value))}>
          {jaren.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <div style={{ display: 'flex', gap: 4, background: '#fff', border: '1px solid var(--cs-border)', borderRadius: 6, padding: 2 }}>
          {['Q1', 'Q2', 'Q3', 'Q4', 'jaar'].map(k => (
            <button key={k} data-testid={`boekhouding-periode-${k}`} className="cs-btn ghost" style={{ padding: '4px 12px', background: kwartaal === k ? 'var(--cs-accent-soft)' : 'transparent', color: kwartaal === k ? 'var(--cs-accent)' : 'inherit' }} onClick={() => setKwartaal(k)}>
              {k === 'jaar' ? t('Heel jaar', 'Heel jaar') : k}
            </button>
          ))}
        </div>
      </div>

      <div className="cs-card padded" style={{ marginBottom: 14 }} data-testid="boekhouder-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, fontWeight: 600 }}>
            <Mail size={16} /> {t('Rapport naar boekhouder', 'Rapport naar boekhouder')}
          </div>
          <button className="cs-btn ghost" data-testid="boekhouder-toggle-panel" onClick={() => setBoekhouderOpen(o => !o)}>{boekhouderOpen ? t('Verbergen', 'Verbergen') : t('Instellen', 'Instellen')}</button>
        </div>
        {boekhouderOpen && (
          <div style={{ marginTop: 12, display: 'grid', gap: 10 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'end', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 220 }}>
                <label className="cs-section-title">{t('E-mailadres boekhouder', 'E-mailadres boekhouder')}</label>
                <input className="cs-input" data-testid="boekhouder-email" type="email" placeholder={t("boekhouder@kantoor.nl","boekhouder@kantoor.nl")} value={boekhouder.accountantEmail || ''} onChange={e => setBoekhouder({ ...boekhouder, accountantEmail: e.target.value })} />
              </div>
              <button className="cs-btn" data-testid="boekhouder-save" disabled={savingBh} onClick={() => saveBoekhouder({ ...boekhouder })}>{t('Opslaan', 'Opslaan')}</button>
              <button className="cs-btn primary" data-testid="boekhouder-verstuur" disabled={sendingBh || !boekhouder.accountantEmail} onClick={async () => {
                const q = kwartaal === 'jaar' ? (Math.floor(new Date().getMonth() / 3) + 1) : parseInt(kwartaal.slice(1));
                setSendingBh(true);
                try { const r = await carsysApi.verstuurRapport(jaar, q); toast.success(`${t('Rapport verstuurd', 'Rapport verstuurd')} (${r.periode}) → ${boekhouder.accountantEmail}`); carsysApi.getRapportSettings().then(setBoekhouder).catch(() => {}); }
                catch (e) { toast.error(t('Versturen mislukt', 'Versturen mislukt'), { description: e.response?.data?.detail || e.message }); }
                finally { setSendingBh(false); }
              }}><Send size={13} /> {sendingBh ? t('Bezig...', 'Bezig...') : t('Stuur nu (Q', 'Stuur nu (Q')}{kwartaal === 'jaar' ? (Math.floor(new Date().getMonth() / 3) + 1) : kwartaal.slice(1)})</button>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
              <input type="checkbox" data-testid="boekhouder-auto" checked={!!boekhouder.autoKwartaalrapport} onChange={e => saveBoekhouder({ ...boekhouder, autoKwartaalrapport: e.target.checked })} />
              {t('Verstuur automatisch elk kwartaal het rapport van het afgelopen kwartaal', 'Verstuur automatisch elk kwartaal het rapport van het afgelopen kwartaal')}
            </label>
            {boekhouder.lastQuarterReportSent && (
              <div style={{ fontSize: 11.5, color: 'var(--cs-text-muted)' }}>{t('Laatst automatisch verstuurd voor', 'Laatst automatisch verstuurd voor')}: {boekhouder.lastQuarterReportSent}</div>
            )}
          </div>
        )}
      </div>

      <div className="cs-two-col">
        {/* BTW-aangifte */}
        <div className="cs-card padded">
          <h3 className="cs-card-title"><Receipt size={16} /> {t('BTW-aangifte', 'BTW-aangifte')} · {periodeLabel}</h3>
          <table className="cs-table">
            <thead><tr><th>{t('Tarief', 'Tarief')}</th><th style={{ textAlign: 'right' }}>{t('Omzet (excl.)', 'Omzet (excl.)')}</th><th style={{ textAlign: 'right' }}>{t('BTW', 'BTW')}</th></tr></thead>
            <tbody>
              {Object.keys(btwOmzetPerTarief).length === 0 && <tr><td colSpan={3} className="cs-empty">{t('Geen omzet in deze periode', 'Geen omzet in deze periode')}</td></tr>}
              {Object.entries(btwOmzetPerTarief).sort((a, b) => b[0] - a[0]).map(([tarief, v]) => (
                <tr key={tarief}>
                  <td>{tarief}%</td>
                  <td style={{ textAlign: 'right' }}>{eur(v.netto)}</td>
                  <td style={{ textAlign: 'right' }}>{eur(v.btw)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: 12, display: 'grid', gap: 6, fontSize: 13 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }} data-testid="btw-verschuldigd"><span>{t('Verschuldigde BTW (over omzet)', 'Verschuldigde BTW (over omzet)')}</span><b>{eur(verschuldigdeBtw)}</b></div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }} data-testid="btw-voorbelasting"><span>{t('Voorbelasting (over inkopen)', 'Voorbelasting (over inkopen)')}</span><b style={{ color: 'var(--cs-accent)' }}>− {eur(voorbelasting)}</b></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '2px solid var(--cs-border)', paddingTop: 8, fontSize: 15 }} data-testid="btw-saldo">
              <b>{btwSaldo >= 0 ? t('Te betalen aan Belastingdienst', 'Te betalen aan Belastingdienst') : t('Terug te ontvangen', 'Terug te ontvangen')}</b>
              <b style={{ color: btwSaldo >= 0 ? 'var(--cs-red)' : 'var(--cs-green)' }}>{eur(Math.abs(btwSaldo))}</b>
            </div>
          </div>
          <p style={{ fontSize: 11, color: 'var(--cs-text-muted)', marginTop: 10 }}>
            {t('Indicatief overzicht op basis van je facturen en kosten. Raadpleeg je boekhouder voor de definitieve aangifte.', 'Indicatief overzicht op basis van je facturen en kosten. Raadpleeg je boekhouder voor de definitieve aangifte.')}
          </p>
        </div>

        {/* Winst & Verlies */}
        <div className="cs-card padded">
          <h3 className="cs-card-title">{resultaat >= 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />} {t('Winst & Verlies', 'Winst & Verlies')} · {periodeLabel}</h3>
          <div style={{ display: 'grid', gap: 6, fontSize: 13 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }} data-testid="wv-omzet"><span>{t('Omzet (excl. BTW)', 'Omzet (excl. BTW)')}</span><b style={{ color: 'var(--cs-green)' }}>{eur(omzetExcl)}</b></div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }} data-testid="wv-automarge"><span>{t('Autohandel marge', 'Autohandel marge')}</span><b style={{ color: 'var(--cs-green)' }}>{eur(autoMarge)}</b></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid var(--cs-border)', paddingTop: 6 }}><span>{t('Totale opbrengsten', 'Totale opbrengsten')}</span><b>{eur(opbrengsten)}</b></div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }} data-testid="wv-kosten"><span>{t('Kosten (excl. BTW)', 'Kosten (excl. BTW)')}</span><b style={{ color: 'var(--cs-red)' }}>− {eur(kostenExcl)}</b></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '2px solid var(--cs-border)', paddingTop: 8, fontSize: 16 }} data-testid="wv-resultaat">
              <b>{t('Resultaat', 'Resultaat')} ({resultaat >= 0 ? t('winst', 'winst') : t('verlies', 'verlies')})</b>
              <b style={{ color: resultaat >= 0 ? 'var(--cs-green)' : 'var(--cs-red)' }}>{eur(resultaat)}</b>
            </div>
          </div>
          {kostenPerCategorie.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--cs-text-muted)', marginBottom: 6 }}>{t('Kosten per categorie', 'Kosten per categorie')}</div>
              <table className="cs-table">
                <tbody>
                  {kostenPerCategorie.map(([c, b]) => (
                    <tr key={c}><td>{c}</td><td style={{ textAlign: 'right' }}>{eur(b)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showRapport && (
        <PrintBoekhoudRapport
          gegevens={gegevens}
          periodeLabel={periodeLabel}
          tot={to}
          btw={{ perTarief: btwOmzetPerTarief, verschuldigd: verschuldigdeBtw, voorbelasting, saldo: btwSaldo }}
          wv={{ omzetExcl, autoMarge, kostenExcl, resultaat, kostenPerCategorie }}
          onClose={() => setShowRapport(false)}
        />
      )}
    </div>
  );
}
